import { AbortError } from '@app/errors/AbortError'

import { isJwtExpired } from '@app/utils/jwt'

import { type AuthSessionManagerInterface, type AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'

export type SupervisedAuthSessionInit = {
  /** The underlying (main-account) session manager used when not supervising. */
  inner: AuthSessionManagerInterface
  /** Current supervision (impersonation) JWT, or null when not supervising. */
  getSupervisionToken: () => string | null
  /** Called when the supervision token is found expired or rejected — logs the supervised session out (clears the cookie/slice, reloads on the client). */
  onSupervisionExpired: () => void
  now?: () => number
}

/**
 * Wraps the main {@link AuthSessionManagerInterface} so that, while supervising,
 * requests authenticate with the impersonation JWT directly. A supervision token
 * has no refresh session, so once it expires — or is rejected upstream
 * (forceRefresh) — the pending request is aborted before hitting the network and
 * `onSupervisionExpired` logs out immediately. While supervising, requests never
 * fall through to the main account's session.
 */
export class SupervisedAuthSession implements AuthSessionManagerInterface {
  private readonly init: SupervisedAuthSessionInit
  private supervisionExpiredFired = false

  constructor(init: SupervisedAuthSessionInit) {
    this.init = init
  }

  async ensureAccessToken(options?: { signal?: AbortSignal }): Promise<string | null> {
    const token = this.init.getSupervisionToken()
    if (token === null) return this.init.inner.ensureAccessToken(options)
    if (isJwtExpired(token, (this.init.now ?? Date.now)())) {
      this.fireSupervisionExpired()
      throw new AbortError('Supervision token expired')
    }
    return token
  }

  peekAccessToken(): string | null {
    return this.init.getSupervisionToken() ?? this.init.inner.peekAccessToken()
  }

  invalidateAccessToken(): void {
    // Only the main account holds a refreshable token; supervision tokens have no
    // refresh session, so this targets the inner manager regardless of mode.
    this.init.inner.invalidateAccessToken()
  }

  async forceRefresh(options?: { signal?: AbortSignal }): Promise<string> {
    // forceRefresh is only invoked after the current token was rejected upstream.
    // A supervision token cannot be refreshed — log out instead of replaying the
    // request under the main account's identity.
    if (this.init.getSupervisionToken() !== null) {
      this.fireSupervisionExpired()
      throw new AbortError('Supervision token rejected')
    }
    return this.init.inner.forceRefresh(options)
  }

  adoptSnapshot(snapshot: AuthSessionSnapshot): void {
    // The exchanged JWT always belongs to the main account, so seed the inner manager.
    this.init.inner.adoptSnapshot(snapshot)
  }

  // Fire the logout handler at most once. It tears down supervision via an
  // authenticated request that re-enters ensureAccessToken before the token is
  // cleared; without this latch that re-entry would recurse until the stack overflows.
  private fireSupervisionExpired(): void {
    if (this.supervisionExpiredFired) return
    this.supervisionExpiredFired = true
    this.init.onSupervisionExpired()
  }
}
